import { useEffect } from "react";

import { useParams, useSearchParams } from "react-router-dom";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

import HeroImage from "@app/analytics/assets/teams_admin_enable.png";
import * as Events from "@app/analytics/tracking/events";
import LINKS from "@app/common/links";
import ROUTES from "@app/common/routes";
import { withQueryParamsUrl } from "@app/common/utils";
import Config from "@app/config";
import { usePageTitle } from "@app/utils/hooks";
import { getApiUrl, getAppUrl } from "@app/utils/utils";

import Alert from "@rui/Alert";
import Button from "@rui/Button";
import HelpTip from "@rui/HelpTip";
import Link from "@rui/Link";
import ReadLogo from "@rui/ReadLogo";
import { Column } from "@rui/Stack";

import {
  Container,
  LeftColumn,
  LeftColumnContent,
  RightColumn,
  RightColumnContent,
} from "./styles";

const TOOLTIP =
  "By enabling, you are authorizing Read to access channels in your Teams Workspace. Read uses this data to generate personalized summaries across channels. Topic summaries are private to the individual user.";

const getEnableUrl = (token: string) => {
  const returnUrl = getAppUrl(window.location.pathname);
  return withQueryParamsUrl(getApiUrl(Config.apps.teams.ADMIN_CONSENT_LINK), {
    encoded_id: token,
    return_url: withQueryParamsUrl(returnUrl, { success: "true" }),
    error_url: withQueryParamsUrl(returnUrl, { success: "false" }),
  });
};

const TeamsAdminEnable = () => {
  usePageTitle("Teams Admin Enable");

  const [searchParams] = useSearchParams();
  const { token } = useParams() as { token: string };

  const isSuccess = searchParams.get("success") === "true";
  const isError = searchParams.get("success") === "false";

  useEffect(() => {
    if (isSuccess || isError) {
      Events.teamsAdminEnable({ status: isError ? "error" : "success" });
    }
  }, [isSuccess, isError]);

  return (
    <Container>
      <LeftColumn>
        <LeftColumnContent variant="left">
          <Column spacing="32px">
            <ReadLogo />
            <Column spacing="16px">
              <Typography
                variant="h3"
                fontWeight={500}
                color="shades.neutral.800"
              >
                Enable channel summaries for your Teams Workspace
              </Typography>
              <Typography variant="bodyPrimaryReg" color="shades.neutral.600">
                Members of your Teams Workspace are using Read AI to generate
                summaries for their conversations. They've requested permission
                to enable Read for channels to generate more robust summaries.
              </Typography>
              {isError && (
                <Alert alignItems="flex-start" embedded severity="error">
                  <Typography component="p" variant="bodySecondaryMed">
                    Error
                  </Typography>
                  <Typography component="p" variant="bodySecondaryReg">
                    There was an error enabling channel summaries for Teams.
                    Please try again.
                  </Typography>
                </Alert>
              )}
              {isSuccess && (
                <Alert alignItems="flex-start" embedded severity="success">
                  <Typography component="p" variant="bodySecondaryMed">
                    Success!
                  </Typography>
                  <Typography component="p" variant="bodySecondaryReg">
                    You've enabled channel summaries for your Teams Workspace.
                  </Typography>
                </Alert>
              )}
            </Column>
            <Column spacing="8px" variant="left">
              {isSuccess ? (
                <Button component={Link} href={getAppUrl(ROUTES.LANDING_PAGE)}>
                  Go to Read
                </Button>
              ) : (
                <Button
                  component={Link}
                  href={getEnableUrl(token)}
                  onClick={() => Events.teamsAdminEnable({ status: "pending" })}
                >
                  Enable for Teams Channels
                </Button>
              )}
              {!isSuccess && (
                <HelpTip
                  enterDelay={500}
                  color="shades.neutral.500"
                  content={TOOLTIP}
                >
                  What is this?
                </HelpTip>
              )}
            </Column>
            {!isSuccess && (
              <Column
                sx={{ backgroundColor: "shades.primary.50" }}
                padding="8px 16px"
                borderRadius="4px"
                spacing="2px"
              >
                <Typography
                  variant="bodySecondaryMed"
                  color="shades.neutral.800"
                >
                  🔐 Privacy is our priority
                </Typography>
                <Typography
                  variant="bodySecondaryReg"
                  color="shades.neutral.600"
                >
                  Read is SOC Type 2 certified for top-notch security. We never
                  sell or share data.{" "}
                  <Link fontWeight={400} href={LINKS.MARKETING.PRIVACY}>
                    Learn more
                  </Link>
                  .
                </Typography>
              </Column>
            )}
          </Column>
        </LeftColumnContent>
      </LeftColumn>
      <RightColumn>
        <RightColumnContent>
          <Column spacing="28px">
            <Typography
              variant="h4"
              color="white"
              textAlign="center"
              fontWeight={500}
              letterSpacing="-0.72px"
              lineHeight={1}
            >
              Super-charge your Teams workflow with channel summaries
            </Typography>
            <Box component="img" src={HeroImage} sx={{ maxWidth: "100%" }} />
            <Typography
              variant="bodySecondaryMed"
              textAlign="center"
              color="white"
              letterSpacing="-0.42px"
            >
              <Link
                underline
                color="shades.primary.200"
                href={LINKS.MARKETING.PRIVACY}
              >
                Learn more
              </Link>{" "}
              about Read Productivity AI + Teams
            </Typography>
          </Column>
        </RightColumnContent>
      </RightColumn>
    </Container>
  );
};

export default TeamsAdminEnable;
